import { DayOfWeek, Duration, LocalDate, LocalDateTime } from "@js-joda/core";
import assertNever from "assert-never";
import classNames from "classnames";
import React from "react";
import { Marker, MarkerProps, Popup, useMap } from "react-map-gl/maplibre";
import { CaregiverVisitBroadcast, Geo, OnboardingVisitId, VisitBroadcastId } from "../messages";
import { Visit } from "../system/AppState/AppVisitsState";
import { getTotalFlexibleVisitHours } from "../utils/flexibleVisitUtils";
import { MAP_AVAILABLE_VISIT_SVG } from "../utils/svgs";
import { getVisitStatus } from "./AvailableVisitsAndTasksList";
import VisitMarkerPopup from "./AvailableVisitsMapVisitMarkerPopup";
import Flex from "./Generic/Flex";

const LIVE_IN_CASE_HOURS_AMOUNT_PER_DAY = 13;

const VisitMarker = (props: {
  position: Geo;
  visit: CaregiverVisitBroadcast;
  isCaregiverOfMedflyt: boolean;
  popupProps: {
    origin: Geo;
    goToVisit: (visitBroadcastId: VisitBroadcastId) => void;
    goToOnboardingVisit: (onboardingVisitId: OnboardingVisitId) => void;
    goToPendingConfirmationVisit: (
      visitBroadcastId: VisitBroadcastId,
      isVisitConfirmed: boolean
    ) => void;
    visitIconWidth: number;
    visitIconHeight: number;
  };
}) => {
  const map = useMap();
  const [isPopupOpen, setPopupOpen] = React.useState<boolean>(false);

  const medflytAgencyIcon = <VisitMarkerForMedflytCaregiver visit={props.visit} />;

  const icon = (
    <div className={" AvailableVisitsLiveMap__markerHitbox"} style={{ margin: "0 -18px 0" }}>
      <span
        className={classNames(
          "AvailableVisitsLiveMap__visitIcon",
          getVisitClass("AvailableVisitsLiveMap__legendItem", props.visit)
        )}
      >
        {MAP_AVAILABLE_VISIT_SVG}
      </span>
    </div>
  );

  const handleClickMarker: MarkerProps["onClick"] = (e) => {
    e.originalEvent.stopPropagation();
    setPopupOpen(true);
    map.current?.flyTo({
      center: { lat: props.position.lat + 0.0028, lng: props.position.lng },
      animate: true,
      duration: 1000,
      zoom: 15,
    });
  };

  return (
    <>
      {isPopupOpen ? (
        <Popup
          className={"AvailableVisitsLiveMap__visitPopoverWrapper"}
          latitude={props.position.lat}
          longitude={props.position.lng}
          onClose={() => setPopupOpen(false)}
          closeOnClick={true}
          closeButton={false}
          anchor={"bottom"}
          offset={15}
        >
          <VisitMarkerPopup {...props.popupProps} visit={props.visit} />
        </Popup>
      ) : null}
      <Marker
        latitude={props.position.lat}
        longitude={props.position.lng}
        onClick={handleClickMarker}
      >
        {props.isCaregiverOfMedflyt ? medflytAgencyIcon : icon}
      </Marker>
    </>
  );
};

interface VisitMarkerForMedflytCaregiverProps {
  visit: CaregiverVisitBroadcast;
}

const VisitMarkerForMedflytCaregiver = (props: VisitMarkerForMedflytCaregiverProps) => {
  const { amount: numberOfHoursPerWeek } = getVisitHoursPerWeek(props.visit);
  const didCaregiverViewedVisit = getVisitStatus(props.visit) === "VIEWED";

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      className={`AvailableVisitsLiveMap__marker_box_medflytAgency_container ${getVisitClass(
        "AvailableVisitsLiveMap__marker_box_medflytAgency_container",
        props.visit
      )}`}
    >
      <div
        className={`AvailableVisitsLiveMap__marker_box_medflytAgency ${getVisitClass(
          "AvailableVisitsLiveMap__marker_box_medflytAgency",
          props.visit
        )}`}
      >
        {!didCaregiverViewedVisit ? <div className="dot_icon"></div> : null}
        <div className="AvailableVisitsLiveMap__marker_box_medflytAgency_textPlaceholer">{`${numberOfHoursPerWeek}h${
          props.visit.publicVisitBroadcastDetails.type !== "Singles" ? "/week" : ""
        }`}</div>
      </div>
      <span className="triangle"></span>
    </Flex>
  );
};

export default VisitMarker;

function getVisitClass(baseClass: string, visit: Visit) {
  const status = getVisitStatus(visit);
  switch (status) {
    case "NEW":
      return `${baseClass}-New`;
    case "REQUESTED":
      return `${baseClass}-Requested`;
    case "PENDING_CONFIRMATION":
      return `${baseClass}-PendingConfirmation`;
    case "VIEWED":
      return `${baseClass}-Viewed`;
    default:
      return assertNever(status);
  }
}

type HoursPerWeek = {
  type: "LiveIn" | "PerWeek" | "Multiple" | "Single";
  amount: number;
};

export function getVisitHoursPerWeek(
  visit: Pick<Visit, "publicVisitBroadcastDetails">
): HoursPerWeek {
  switch (visit.publicVisitBroadcastDetails.type) {
    case "Pattern": {
      const now = LocalDate.now();
      let isLiveIn = false;
      let totalHours = 0;
      const daysOfWeek = visit.publicVisitBroadcastDetails.weeklyPattern.daysOfWeek;

      for (const shifts of Object.values(daysOfWeek)) {
        for (const shift of shifts) {
          const startTime = LocalDateTime.of(now, shift.startAt);
          let endTime = LocalDateTime.of(now, shift.endAt);
          let duration = Duration.between(startTime, endTime).toHours();

          // In case of overnight shift (e.g. 10pm - 2am)
          if (startTime.isAfter(endTime)) {
            endTime = endTime.plusDays(1);
            duration = Duration.between(startTime, endTime).toHours();
          }

          // if the visit is a live in case
          if (startTime.equals(endTime)) {
            isLiveIn = true;
            duration = LIVE_IN_CASE_HOURS_AMOUNT_PER_DAY;
          }

          totalHours += duration;
        }
      }

      return isLiveIn
        ? { type: "LiveIn", amount: totalHours }
        : { type: "PerWeek", amount: totalHours };
    }
    case "Singles": {
      let totalHours = 0;
      let isLiveInCase = false;

      const hoursByDayOfWeek = new Map<DayOfWeek, number[]>();

      for (const instance of visit.publicVisitBroadcastDetails.visitInstances) {
        const dayOfWeek = instance.date.startDateTime.dayOfWeek();
        const hours = hoursByDayOfWeek.get(dayOfWeek) ?? [];
        const totalInstanceHours = Duration.between(
          instance.date.startDateTime,
          instance.date.endDateTime
        ).toHours();

        isLiveInCase = instance.date.startDateTime
          .toLocalTime()
          .equals(instance.date.endDateTime.toLocalTime());

        hours.push(isLiveInCase ? LIVE_IN_CASE_HOURS_AMOUNT_PER_DAY : totalInstanceHours);

        hoursByDayOfWeek.set(dayOfWeek, hours);
      }

      const numberOfWeeks = Math.max(
        ...Array.from(hoursByDayOfWeek.values()).map((hours) => hours.length)
      );

      for (const hours of hoursByDayOfWeek.values()) {
        totalHours += hours.reduce((a, b) => a + b, 0) / numberOfWeeks;
      }

      if (isLiveInCase) {
        return { type: "LiveIn", amount: Math.round(totalHours) };
      }

      return visit.publicVisitBroadcastDetails.visitInstances.length === 1
        ? { type: "Single", amount: Math.round(totalHours) }
        : { type: "Multiple", amount: Math.round(totalHours) };
    }
    case "FlexiblePattern": {
      const { flexibleVisitParams } = visit.publicVisitBroadcastDetails;

      const hours = getTotalFlexibleVisitHours(flexibleVisitParams);
      return { type: "PerWeek", amount: hours };
    }
    default:
      return assertNever(visit.publicVisitBroadcastDetails);
  }
}
